import React, { useState, useEffect } from 'react';
import { FaBoxOpen, FaUser, FaCalendarAlt, FaCheckCircle, FaClock, FaTruck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useCurrency } from './CurrencyContext';
import { getCurrencyDecimals } from './currencyHelpers';
import config from './config';
import { useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

const TrackOrderPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { getCurrencySymbol } = useCurrency();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();
  const [selectedOrder] = useState(null);
  const [error, setError] = useState(null);
  const [loading] = useState(false);

  const getStatusName = (status) => {
    const map = { 
      'Order Confirmed': 'confirmed', 
      'Preparing': 'preparing', 
      'Ready for Pickup': 'readyForPickup', 
      'ReadyForPickup': 'readyForPickup', 
      'Delivered': 'delivered', 
      'Completed': 'completed', 
      'Out for Delivery': 'outForDelivery', 
      'Pending': 'pending', 
      'Processing': 'processing', 
      'processingpayment': 'processingPayment', 
      'In Progress': 'inProgress' 
    };
    const key = map[status] || 'unknown';
    return t(`trackOrder.status.${key}`, status);
  };

  useEffect(() => {
    if (!user?.Id) { setOrders([]); return; }
    let isMounted = true, intervalId = null;
    const fetchUserOrders = async () => {
      if (!isMounted) return;
      try {
        const res = await axios.get(`${config.API_BASE_URL}/Orders/user/${user.Id}`);
        const allOrders = Array.isArray(res.data) ? res.data : [];
        setOrders(allOrders.filter(o => { 
          const status = o.OrderStatus || o.status || o.Status; 
          return status !== 'Delivered' && status !== 'Completed' && status !== 'Served' && o.OrderId; 
        }));
      } catch { setOrders([]); }
    };
    fetchUserOrders();
    intervalId = setInterval(fetchUserOrders, 30000);
    return () => { isMounted = false; if (intervalId) clearInterval(intervalId); };
  }, [user?.Id]);

  const handleSelectOrder = (order) => {
    if (!order?.OrderId) { setError("OrderId is required."); return; }
    if (!user?.Id) { setError("User information is missing."); return; }
    const status = order.OrderStatus || order.status || order.Status;
    const paymentMethod = order.PaymentMethod || order.paymentMethod;
    if (status === 'processingpayment' || paymentMethod === 'wallet') navigate(`/prepayment-order/${order.OrderId}`);
    else navigate(`/order-status/${order.OrderId}`);
  };

  return (
    <>
      {user ? (
        <div style={{ maxWidth: 700, margin: '40px auto', padding: 0, fontFamily: 'Cairo, Tahoma, Arial, sans-serif' }}>
          <div style={{ background: theme.palette.primary.main, borderRadius: '18px 18px 0 0', padding: '32px 24px 18px 24px', color: theme.palette.custom.white, textAlign: 'center', boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.10)}` }}>
            <FaBoxOpen size={38} style={{ marginBottom: -8, marginLeft: 8 }} />
            <span style={{ fontFamily: 'Cairo, Tahoma, Arial, sans-serif', fontSize: '2.1rem', fontWeight: 700, letterSpacing: 1 }}>{t('trackOrder.title')}</span>
          </div>
          <div style={{ background: theme.palette.background.paper, borderRadius: '0 0 18px 18px', boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.08)}`, padding: 32 }}>
            {orders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <FaBoxOpen size={60} color={theme.palette.custom.border} style={{ marginBottom: 16 }} />
                <div style={{ fontSize: 22, color: theme.palette.text.secondary, fontWeight: 500 }}>{t('trackOrder.noOrders')}</div>
                <div style={{ color: theme.palette.text.disabled, marginTop: 8 }}>{t('trackOrder.checkLogin')}</div>
              </div>
            )}
            {orders.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 24, justifyContent: 'center' }}>
                {orders.map(order => {
                  // ✅ Use order's currency directly
                  const orderCurrency = order?.Currency || 'YER';
                  const currencySymbol = getCurrencySymbol(orderCurrency);
                  const decimals = getCurrencyDecimals(orderCurrency);

                  const status = (order.status || order.OrderStatus);
                  let statusColor = theme.palette.warning.main, statusIcon = <FaClock style={{ marginLeft: 6 }} />;
                  if (status === 'Delivered' || status === 'Completed') { 
                    statusColor = theme.palette.success.main; 
                    statusIcon = <FaCheckCircle style={{ marginLeft: 6 }} />; 
                  }
                  else if (status === 'Out for Delivery') { 
                    statusColor = theme.palette.info.main; 
                    statusIcon = <FaTruck style={{ marginLeft: 6 }} />; 
                  }
                  const statusName = getStatusName(status);
                  let items = order.items || (order.OrderItemsJson ? JSON.parse(order.OrderItemsJson) : []);
                  
                  return (
                    <div 
                      key={order.OrderId} 
                      style={{ 
                        position: 'relative', 
                        border: selectedOrder?.OrderId === order.OrderId ? `2px solid ${theme.palette.primary.main}` : `1.5px solid ${theme.palette.custom.border}`, 
                        borderRadius: 14, 
                        padding: 22, 
                        minWidth: 240, 
                        maxWidth: 320, 
                        background: selectedOrder?.OrderId === order.OrderId ? theme.palette.custom.primaryLight : theme.palette.background.default, 
                        cursor: 'pointer', 
                        boxShadow: selectedOrder?.OrderId === order.OrderId ? `0 2px 12px ${alpha(theme.palette.primary.main, 0.22)}` : `0 1px 6px ${alpha(theme.palette.custom.border, 0.22)}`, 
                        transition: 'box-shadow 0.2s, border 0.2s', 
                        marginBottom: 8, 
                        overflow: 'hidden' 
                      }} 
                      onClick={() => handleSelectOrder(order)} 
                      onMouseOver={e => e.currentTarget.style.boxShadow = `0 4px 18px ${alpha(theme.palette.primary.main, 0.22)}`} 
                      onMouseOut={e => e.currentTarget.style.boxShadow = selectedOrder?.OrderId === order.OrderId ? `0 2px 12px ${alpha(theme.palette.primary.main, 0.22)}` : `0 1px 6px ${alpha(theme.palette.custom.border, 0.22)}`}
                    >
                      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: `${theme.palette.primary.main}`, opacity: 0.09, zIndex: 1, pointerEvents: 'none' }} />
                      <div style={{ position: 'relative', zIndex: 2 }}>
                        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 6, color: theme.palette.text.primary, letterSpacing: 0.5 }}>
                          <span>
                            <FaBoxOpen style={{ marginLeft: 7, color: theme.palette.primary.main, verticalAlign: -2 }} />
                            {t('trackOrder.orderNumber')} {order.OrderId != null ? String(order.OrderId) : t('common.unknown')}
                          </span>
                        </div>
                        <div style={{ marginBottom: 8, color: theme.palette.text.secondary, fontSize: 15 }}>
                          <FaUser style={{ marginLeft: 6, color: theme.palette.text.secondary }} />
                          {order.customerName || order.CustomerName || t('common.unknown')}
                        </div>
                        <div style={{ marginBottom: 8, color: theme.palette.text.secondary, fontSize: 15 }}>
                          <FaCalendarAlt style={{ marginLeft: 6, color: theme.palette.text.secondary }} />
                          {order.CreatedDate ? new Date(order.CreatedDate).toLocaleString() : (order.date || order.OrderDate || t('common.unknown'))}
                        </div>
                        <div style={{ marginBottom: 0 }}>
                          <span style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            background: statusColor, 
                            color: theme.palette.custom.white, 
                            borderRadius: 12, 
                            fontWeight: 600, 
                            fontSize: 14, 
                            padding: '4px 14px', 
                            boxShadow: `0 1px 4px ${alpha(theme.palette.custom.border, 0.55)}`, 
                            letterSpacing: 0.2 
                          }}>
                            {statusIcon}{statusName}
                          </span>
                        </div>
                        
                        <div style={{ marginTop: 12 }}>
                          {Array.isArray(items) && items.length > 0 && items.slice(0, 3).map((item, idx) => {
                            // ✅ Enhanced image handling
                            const rawImageData = item.image || item.ProductImage || item.ImageUrl || item.imageJson || '';
                            let img = '';
                            if (rawImageData) {
                              if (typeof rawImageData === 'object' && !Array.isArray(rawImageData)) {
                                if (rawImageData.thumbnail) {
                                  img = rawImageData.thumbnail.startsWith('http') 
                                    ? rawImageData.thumbnail 
                                    : `${config.SITE_BASE_URL}${rawImageData.thumbnail}`;
                                } else if (rawImageData.medium) {
                                  img = rawImageData.medium.startsWith('http') 
                                    ? rawImageData.medium 
                                    : `${config.SITE_BASE_URL}${rawImageData.medium}`;
                                }
                              } else if (typeof rawImageData === 'string') {
                                if (rawImageData.startsWith('http') || rawImageData.startsWith('data:image')) {
                                  img = rawImageData;
                                } else if (rawImageData.startsWith('/images/')) {
                                  img = `${config.SITE_BASE_URL}${rawImageData}`;
                                } else {
                                  img = rawImageData;
                                }
                              }
                            }
                            if (!img) img = '/logo-storesye.png';
                            
                            const qty = item.quantity || item.Quantity || 1;
                            const name = item.name || item.ProductName || item.productName || 'Product';
                            const description = item.Description || item.description || '';
                            
                            // ✅ Use price directly from item data (already in order currency)
                            const displayPrice = Number(item.Price || item.price || 0);
                            
                            // ✅ Get selected color
                            const selectedColor = item.SelectedColor || item.selectedColor || null;
                            
                            return (
                              <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <img 
                                    src={img} 
                                    alt={name} 
                                    style={{ 
                                      width: 40, 
                                      height: 40, 
                                      objectFit: 'cover', 
                                      borderRadius: 8, 
                                      marginRight: 8, 
                                      border: '1px solid #eee' 
                                    }} 
                                  />
                                  <div style={{ flexGrow: 1 }}>
                                    <span style={{ fontWeight: 600, fontSize: 15, display: 'block' }}>
                                      {name}
                                    </span>
                                    
                                    {/* ✅ Display Selected Color */}
                                    {selectedColor && (
                                      <span style={{ 
                                        display: 'inline-block',
                                        fontSize: 11, 
                                        color: theme.palette.secondary.main,
                                        fontWeight: 600,
                                        marginTop: 2,
                                        padding: '2px 6px',
                                        borderRadius: 4,
                                        border: `1px solid ${theme.palette.secondary.main}`,
                                        backgroundColor: alpha(theme.palette.secondary.main, 0.1)
                                      }}>
                                        🎨 {selectedColor}
                                      </span>
                                    )}
                                    
                                    {/* ✅ Display description if available */}
                                    {description && (
                                      <span style={{ 
                                        display: 'block',
                                        fontSize: 11, 
                                        color: theme.palette.text.secondary,
                                        marginTop: 2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '100%'
                                      }}>
                                        {description}
                                      </span>
                                    )}
                                    
                                    <span style={{ display: 'block', color: theme.palette.primary.main, fontWeight: 600, fontSize: 14, marginTop: 2 }}>
                                      {displayPrice.toFixed(decimals)} {currencySymbol}
                                    </span>
                                  </div>
                                  <span style={{ color: theme.palette.secondary.main, fontWeight: 600, minWidth: 28, textAlign: 'center' }}>
                                    × {qty}
                                  </span>
                                </div>
                                
                                {/* ✅ Custom Product Subproducts */}
                                {item.isCustom && Array.isArray(item.SubProducts) && item.SubProducts.length > 0 && (
                                  <div style={{ marginTop: 4, marginLeft: 12 }}>
                                    <span style={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: 13 }}>
                                      {t('customProduct.selectedSubproducts', 'Selected Options')}:
                                    </span>
                                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                                      {item.SubProducts.map((sp, spIdx) => {
                                        if (!sp.Name) return null;
                                        
                                        // ✅ Use price directly from subproduct data (already in order currency)
                                        const spPrice = Number(sp.Price || sp.price || 0);
                                        
                                        return (
                                          <li key={sp.Id || spIdx} style={{ fontSize: 13, marginBottom: 2 }}>
                                            <span style={{ fontWeight: 600 }}>{sp.Name}</span>
                                            {spPrice > 0 && (
                                              <span style={{ color: theme.palette.primary.main, marginLeft: 6 }}>
                                                {t('price', 'Price')}: {spPrice.toFixed(decimals)} {currencySymbol}
                                              </span>
                                            )}
                                            {sp.Quantity !== undefined && sp.Quantity !== null && sp.Quantity !== '' && sp.Quantity !== 'null' && sp.Quantity !== 0 && (
                                              <span style={{ color: theme.palette.secondary.main, marginLeft: 6 }}>
                                                {t('quantity', 'Qty')}: {sp.Quantity}
                                              </span>
                                            )}
                                            {sp.SizeType && (
                                              <span style={{ color: theme.palette.text.secondary, marginLeft: 6 }}>
                                                {t('sizeType', 'Size Type')}: {sp.SizeType}
                                              </span>
                                            )}
                                            {sp.Height !== undefined && sp.Height !== null && sp.Height !== '' && sp.Height !== 'null' && sp.Height !== 'undefined' && sp.Height !== 0 && (
                                              <span style={{ color: theme.palette.text.secondary, marginLeft: 6 }}>
                                                {t('height', 'Height')}: {sp.Height}
                                              </span>
                                            )}
                                            {sp.Width !== undefined && sp.Width !== null && sp.Width !== '' && sp.Width !== 'null' && sp.Width !== 'undefined' && sp.Width !== 0 && (
                                              <span style={{ color: theme.palette.text.secondary, marginLeft: 6 }}>
                                                {t('width', 'Width')}: {sp.Width}
                                              </span>
                                            )}
                                            {sp.Description && (
                                              <span style={{ color: theme.palette.text.disabled, marginLeft: 6 }}>
                                                {sp.Description}
                                              </span>
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {Array.isArray(items) && items.length > 3 && (
                            <div style={{ textAlign: 'center', color: theme.palette.text.secondary, fontSize: 13, marginTop: 4 }}>
                              +{items.length - 3} {t('orderHistory.moreItems')}
                            </div>
                          )}
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                          <button 
                            style={{ 
                              background: theme.palette.primary.main, 
                              color: theme.palette.custom.white, 
                              border: 'none', 
                              borderRadius: 8, 
                              fontWeight: 700, 
                              fontSize: 16, 
                              padding: '8px 32px', 
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`, 
                              cursor: 'pointer', 
                              zIndex: 3 
                            }} 
                            onClick={e => { e.stopPropagation(); handleSelectOrder(order); }}
                          >
                            {t('account.trackOrder')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {loading && (
              <div style={{ textAlign: 'center', color: theme.palette.primary.main, fontWeight: 500, fontSize: 18 }}>
                {t('common.loading')}
              </div>
            )}
            {error && (
              <div style={{ color: theme.palette.error.main, marginTop: 16, textAlign: 'center' }}>
                {error}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: 'Cairo, Tahoma, Arial, sans-serif' }}>
          <FaBoxOpen size={60} color={theme.palette.custom.border} style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 22, color: theme.palette.text.secondary, fontWeight: 500 }}>
            {t('trackOrder.loginRequiredTitle')}
          </div>
          <div style={{ color: theme.palette.text.disabled, marginTop: 8 }}>
            {t('trackOrder.loginMsg')}
          </div>
        </div>
      )}
    </>
  );
};

export default TrackOrderPage;